import React, { useState } from 'react';
import { X, CheckCircle, XCircle, Download, Eye, EyeOff, Shield, User, MapPin, Phone, Mail } from 'lucide-react';
import { useUserStore } from '../../stores/userStore';
import { formatDate } from '../../utils/formatters';
import LZString from 'lz-string';

interface VerificationDetailsModalProps {
  userId: string;
  type: 'id' | 'address';
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
}

const VerificationDetailsModal: React.FC<VerificationDetailsModalProps> = ({
  userId,
  type,
  onApprove,
  onReject,
  onClose
}) => {
  const { users } = useUserStore();
  const [showDocument, setShowDocument] = useState(false);
  const [documentImage, setDocumentImage] = useState<string | null>(null);

  const user = users[userId];
  if (!user || !user.verifications?.[type]) return null;

  const verification = user.verifications[type];
  const documentType = verification.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

  const handleViewDocument = () => {
    if (!verification.documentUrl) return;

    if (!showDocument) {
      try {
        const decompressedData = LZString.decompressFromUTF16(verification.documentUrl);
        setDocumentImage(decompressedData);
      } catch (error) {
        console.error('Error decompressing document:', error);
        setDocumentImage(null);
      }
    } else {
      setDocumentImage(null);
    }
    setShowDocument(!showDocument);
  };

  const handleDownload = () => {
    if (!verification.documentUrl) return;

    try {
      const decompressedData = LZString.decompressFromUTF16(verification.documentUrl);
      const link = document.createElement('a');
      link.href = decompressedData;
      link.download = `verification-${type}-${user.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {type === 'id' ? 'ID Verification' : 'Address Verification'}
            </h3>
            <p className="text-sm text-gray-500">
              Submitted by {user.fullName || user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* User Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-gray-900 mb-4">User Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-medium">{user.fullName || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{user.phone || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{user.location || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Document Details */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Document Type</p>
              <p className="mt-1 font-medium">{documentType}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Submission Date</p>
              <p className="mt-1 font-medium">{formatDate(verification.uploadDate)}</p>
            </div>
          </div>

          {/* Document Preview */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">Document Preview</h4>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleViewDocument}
                  className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {showDocument ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-1.5" />
                      Hide Document
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-1.5" />
                      Show Document
                    </>
                  )}
                </button>
                {verification.documentUrl && (
                  <button
                    onClick={handleDownload}
                    className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    Download
                  </button>
                )}
              </div>
            </div>

            {showDocument ? (
              <div className="relative aspect-[3/2] bg-gray-100 rounded-lg overflow-hidden">
                {documentImage ? (
                  <img
                    src={documentImage}
                    alt="Verification document"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    Failed to load document
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-[3/2] bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                <p className="text-center">
                  Document hidden for security<br />
                  Click "Show Document" to view
                </p>
              </div>
            )}
          </div>

          {/* Verification Guidelines */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 mb-2">Verification Guidelines</h4>
            <ul className="space-y-2 text-sm text-yellow-700">
              <li>• Verify that the document is valid and not expired</li>
              <li>• Check that all information is clearly visible</li>
              <li>• Confirm the document type matches the submission</li>
              <li>• Ensure the document belongs to the user</li>
              {type === 'address' && (
                <li>• Verify that the address matches the user's profile</li>
              )}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onReject}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
          >
            <XCircle className="h-5 w-5 mr-2" />
            Reject
          </button>
          <button
            onClick={onApprove}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <CheckCircle className="h-5 w-5 mr-2" />
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationDetailsModal;